import React, { useState, useContext } from "react";
import { httpsCallable } from "firebase/functions";
import { functions } from "../../../../config/firebaseConfig";
import AIFeedbackButton from "./AIFeedbackButton";
import formatFeedback from "./helpers/formatFeedback";
import { UserContext } from "../../../../App";

const AudioFeedback = ({
  audioURL,
  onFeedbackGenerated,
  isTeacher = false,
}) => {
  const [feedback, setFeedback] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const { role } = useContext(UserContext);

  const getFeedback = async () => {
    console.log("Getting feedback for audio URL:", audioURL);
    setIsLoading(true);
    setError("");

    try {
      // Step 1: Fetch the audio file
      const response = await fetch(audioURL);
      if (!response.ok) {
        throw new Error(
          `Failed to fetch audio: ${response.status} ${response.statusText}`
        );
      }
      const audioBlob = await response.blob();
      console.log("Audio blob fetched:", audioBlob);

      // Step 2: Convert the audio to WAV format
      const audioContext = new (window.AudioContext ||
        window.webkitAudioContext)();
      const audioBuffer = await audioContext.decodeAudioData(
        await audioBlob.arrayBuffer()
      );
      const wavBlob = await convertToWav(audioBuffer);

      // Step 3: Convert the blob to base64
      const base64Data = await blobToBase64(wavBlob);

      // Step 4: Use the cloud function for transcription
      const openAIProxy = httpsCallable(functions, "openAIProxy");
      const transcriptionResponse = await openAIProxy({
        requestType: "transcription",
        params: {
          fileData: base64Data.split(",")[1], // Remove the data URL prefix
          model: "whisper-1",
        },
      });

      const transcription = transcriptionResponse.data.text;

      // Step 5: Get AI feedback on the transcription using the cloud function
      const feedbackResponse = await openAIProxy({
        requestType: "chat",
        params: {
          model: "gpt-4o-mini",
          messages: [
            {
              role: "system",
              content: "You are a helpful language teaching assistant.",
            },
            {
              role: "user",
              content: `Please provide feedback on the following spoken text, focusing on pronunciation, grammar, and fluency: "${transcription}". Do not mention spoken text or transcription.`,
            },
          ],
        },
      });

      const aiFeedback = feedbackResponse.data.choices[0].message.content;
      setFeedback(aiFeedback);
      if (onFeedbackGenerated) {
        onFeedbackGenerated(aiFeedback);
      }
    } catch (err) {
      console.error("Detailed error in audio feedback:", err);
      setError(`Failed to get feedback: ${err.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  // Helper function to convert Blob to base64
  const blobToBase64 = (blob) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  };

  const convertToWav = async (audioBuffer) => {
    const numberOfChannels = audioBuffer.numberOfChannels;
    const length = audioBuffer.length * numberOfChannels;
    const sampleRate = audioBuffer.sampleRate;
    const wavBuffer = new ArrayBuffer(44 + length * 2);
    const view = new DataView(wavBuffer);

    // Write WAV header
    writeString(view, 0, "RIFF");
    view.setUint32(4, 36 + length * 2, true);
    writeString(view, 8, "WAVE");
    writeString(view, 12, "fmt ");
    view.setUint32(16, 16, true);
    view.setUint16(20, 1, true);
    view.setUint16(22, numberOfChannels, true);
    view.setUint32(24, sampleRate, true);
    view.setUint32(28, sampleRate * numberOfChannels * 2, true);
    view.setUint16(32, numberOfChannels * 2, true);
    view.setUint16(34, 16, true);
    writeString(view, 36, "data");
    view.setUint32(40, length * 2, true);

    // Write audio data
    const offset = 44;
    for (let i = 0; i < audioBuffer.numberOfChannels; i++) {
      const channel = audioBuffer.getChannelData(i);
      for (let j = 0; j < channel.length; j++) {
        const sample = Math.max(-1, Math.min(1, channel[j]));
        view.setInt16(
          offset + (j * numberOfChannels + i) * 2,
          sample * 0x7fff,
          true
        );
      }
    }

    return new Blob([wavBuffer], { type: "audio/wav" });
  };

  const writeString = (view, offset, string) => {
    for (let i = 0; i < string.length; i++) {
      view.setUint8(offset + i, string.charCodeAt(i));
    }
  };

  // Only allow AI feedback for plus roles and admin
  const canUseAIFeedback = [
    "studentplus",
    "teacherplus",
    "admin",
    "technicianplus",
  ].includes(role);

  return (
    <div className="audio-feedback">
      {!feedback && canUseAIFeedback && (
        <AIFeedbackButton
          onClick={getFeedback}
          className={`mt-2 ml-2 ${isLoading ? "disabled" : ""}`}
          isLoading={isLoading}
          label={isTeacher ? "Get AI Feedback" : "Chat with AI"}
        />
      )}
      {error && <p className="error-message">{error}</p>}
      {feedback && !isTeacher && (
        <div className="teacher-feedback">
          <div className="feedback-header">
            <h4>AI Feedback:</h4>
          </div>
          <div className="feedback-content-wrapper">
            <div className="feedback-content">
              <div className="ai-feedback-container">
                <div className="ai-feedback-text">
                  <p
                    dangerouslySetInnerHTML={{
                      __html: formatFeedback.formatForDisplay(feedback),
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AudioFeedback;
